<template>
  <div v-click-outside="closeSubMenu" class="flex md:relative">
    <RevButtonIcon
      v-if="!authenticated"
      :aria-label="i18n(translations.userIconGuestAltText)"
      data-qa="icon-avatar"
      :icon="IconAvatar"
      :title="i18n(translations.userIconGuestAltText)"
      :to="iconAvatarLink"
      :tracking="{
        zone: 'header',
        name: 'register',
      }"
      variant="secondary"
    >
    </RevButtonIcon>

    <!--
      There is an hydration error that cause the button to be non-responsive
      see: https://imperatorz.slack.com/archives/C085K6UP5K9
      As a workaround we create a disabled button server side and wait for the hydration to be completed to create the actual button.
    -->
    <RevButtonBase
      v-if="authenticated && !hydrationCompleted"
      :aria-label="i18n(translations.userIconAltText)"
      data-qa="icon-avatar-logged-in"
      :disabled="true"
      :title="i18n(translations.userIconAltText)"
    >
      <RevAvatar
        class="!size-40 !text-[1.25rem]"
        data-test="icon-login"
        :label="avatarLabel"
        size="medium"
        :thumbnail="authIconAvatar"
      />
    </RevButtonBase>
    <RevButtonBase
      v-if="authenticated && hydrationCompleted"
      :aria-label="i18n(translations.userIconAltText)"
      data-qa="icon-avatar-logged-in"
      :disabled="!hydrationCompleted"
      :title="i18n(translations.userIconAltText)"
      :tracking="{
        zone: 'header',
        name: 'dashboard',
      }"
      @click="toggleSubMenu"
    >
      <RevAvatar
        class="!size-40 !text-[1.25rem]"
        data-test="icon-login"
        :label="avatarLabel"
        size="medium"
        :thumbnail="authIconAvatar"
      />
    </RevButtonBase>

    <ul
      class="bg-overlap-default-low shadow-long rounded-sm absolute bottom-4 left-1/2 z-50 w-[calc(100vw-1.5rem)] -translate-x-1/2 translate-y-full list-none divide-y p-0 md:-bottom-16 md:left-auto md:right-0 md:w-256 md:translate-x-0"
      :class="{ block: isVisible, hidden: !isVisible }"
    >
      <li>
        <RevLink
          :to="resolveLocalizedRoute({ name: DASHBOARD.KYC.PROFILE })"
          :tracking="{
            zone: 'header',
            name: 'subnav_my_account',
          }"
          :underlined="false"
          @click="closeSubMenu"
        >
          <span class="body-2 flex w-full items-center px-16 py-20">
            <IconAvatar class="mr-20 h-20 w-20 md:h-24 md:w-24" />

            {{ i18n(translations.myAccount) }}
          </span>
        </RevLink>
      </li>
      <li class="border-static-default-low">
        <RevLink
          :to="resolveLocalizedRoute({ name: DASHBOARD.MY_ORDERS.SELF })"
          :tracking="{
            zone: 'header',
            name: 'subnav_my_orders',
          }"
          :underlined="false"
          @click="closeSubMenu"
        >
          <span class="body-2 flex w-full items-center px-16 py-20">
            <IconPackage class="mr-20 h-20 w-20 md:h-24 md:w-24" />

            {{ i18n(translations.myOrders) }}
          </span>
        </RevLink>
      </li>

      <li class="border-static-default-low">
        <RevLink
          :to="resolveLocalizedRoute({ name: DASHBOARD.FAVORITES })"
          :tracking="{
            zone: 'header',
            name: 'subnav_my_favorites',
          }"
          :underlined="false"
          @click="closeSubMenu"
        >
          <span class="body-2 flex w-full items-center px-16 py-20">
            <IconHeart class="mr-20 h-20 w-20 md:h-24 md:w-24" />
            {{ i18n(translations.myFavorites) }}
          </span>
        </RevLink>
      </li>

      <li v-if="shouldDisplayMySalesLink" class="border-static-default-low">
        <RevLink
          :to="resolveLocalizedRoute({ name: DASHBOARD.MY_SALES })"
          :tracking="{
            zone: 'header',
            name: 'subnav_my_tradeins',
          }"
          :underlined="false"
          @click="closeSubMenu"
        >
          <span class="body-2 flex w-full items-center px-16 py-20">
            <IconSwap class="mr-20 h-20 w-20 md:h-24 md:w-24" />

            {{ i18n(translations.mySales) }}
          </span>
        </RevLink>
      </li>

      <li class="border-static-default-low">
        <RevButtonBase
          class="cursor-pointer appearance-none border-none bg-transparent text-inherit no-underline hover:bg-transparent hover:text-inherit hover:no-underline"
          data-test="logout-button"
          @click="handleLogout"
        >
          <span class="body-2 flex w-full items-center px-16 py-20">
            <IconPower class="mr-20 h-20 w-20 md:h-24 md:w-24" />

            {{ i18n(translations.logOut) }}
          </span>
        </RevButtonBase>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale } from '#imports'
import { computed, onMounted, ref } from 'vue'

import { deleteIntercomCookie } from '@backmarket/nuxt-layer-care-platform/intercom/utils/intercom'
import { useAuthStore } from '@backmarket/nuxt-layer-oauth/useAuthStore'
import { useLogout } from '@backmarket/nuxt-layer-oauth/useLogout'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { clickOutside as vClickOutside } from '@backmarket/utils/directives/ClickOutside'
import { RevAvatar } from '@ds/components/Avatar'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevLink } from '@ds/components/Link'
import { IconAvatar } from '@ds/icons/IconAvatar'
import { IconHeart } from '@ds/icons/IconHeart'
import { IconPackage } from '@ds/icons/IconPackage'
import { IconPower } from '@ds/icons/IconPower'
import { IconSwap } from '@ds/icons/IconSwap'
import { storeToRefs } from 'pinia'

import { ROUTES } from '~/scopes/auth/route-names'
import { DASHBOARD } from '~/scopes/dashboard/routes'

import translations from './UserIconAndSubMenu.translations'
import { useAvatar } from './composables/useAvatar'

const { trackClick } = useTracking()
const i18n = useI18n()

const authStore = useAuthStore()
const userStore = useUserStore()

const { user } = storeToRefs(userStore)

const { authenticated } = storeToRefs(authStore)

const { logout } = useLogout()

const { features } = useMarketplace()

const resolveLocalizedRoute = useRouteLocationWithLocale()

const { label: avatarLabel, thumbnail: authIconAvatar } = useAvatar(
  computed(() => user.value.firstName),
)

const isVisible = ref(false)

const hydrationCompleted = ref(false)

onMounted(() => {
  hydrationCompleted.value = true
})

function toggleSubMenu() {
  if (authenticated.value) {
    isVisible.value = !isVisible.value
  }
}
function closeSubMenu() {
  isVisible.value = false
}
function handleClick(name: string) {
  closeSubMenu()
  trackClick({
    zone: 'header',
    name,
  })
}

function handleLogout() {
  handleClick('subnav_logout')
  deleteIntercomCookie()
  logout()
}

const shouldDisplayMySalesLink = computed(
  () => features.buyback?.headerButtonEnabled,
)

const locale = useI18nLocale()
const iconAvatarLink = computed(() => ({
  name: ROUTES.AUTH.LOGIN,
  query: {
    next: `/${locale}/dashboard/orders`,
  },
  params: { locale },
}))
</script>
